import * as React from 'react';
import { NonRouterModal } from './Modal';
import { Button, Checkbox, Divider, FormControl, FormControlLabel, IconButton, Input, InputLabel, Paper, Stack, Switch, TextField, Typography } from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import { blue } from '@mui/material/colors';
import Grid from '@mui/material/Grid2';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import CheckIcon from '@mui/icons-material/Check';
import { AlertProps } from '@mui/material/Alert';

import { httpPost } from '../Web';
import { Workshop, getWorkshop, getWorkshopInterfaces } from '../types/Workshop';
import { InterfaceSearchAutocomplete } from '../subs/InterfaceSearchAutocomplete';
import { BlueSwitch } from '../subs/StyledSwitches';
import { Interface, enumerateInterfaces } from '../types/Interface';
import { WorkshopInterface } from '../types/WorkshopInterface';
import { Company, getCompanies } from '../types/Company';
import { TestCombination, TestComboSupportVal, TestCombinationStatus, getTestCombinationList, getTestComboSupport } from '../types/TestCombinations';
import { WorkshopProductCombination, GetEmptyWSPTC, getProductCombinations, updateProdTestCombination } from '../types/WorkshopProductCombination';

import { Product, getProduct, getProducts } from '../types/Product';
import { ProductReleaseStatus, WorkshopProduct, WorkshopProductStatus, deleteWorkshopProductInterface, updateWorkshopProduct, updateWorkshopProductInterface } from '../types/WorkshopProducts';

import AddIcon from '@mui/icons-material/Add';
import { UserContext } from '../UserProvider';
import { WorkshopProductInterface } from '../types/WorkshopProductInterface';
import { ApiResponse } from '../types/ApiResponse';
import { useSnackbar } from '../../contexts/SnackBarContext';

interface SelectedInterface {
    interfaceId: number;
    supportedByProduct: boolean;
}

export const UpdateWsProductForm2 = ({ wpId, workshopProducts, setWorkshopProducts, workshopProductsInterfaces, setWorkshopProductInterfaces,
    openState, setOpenState }:
    {
        wpId: number, workshopProducts: WorkshopProduct[],
        setWorkshopProducts: React.Dispatch<React.SetStateAction<WorkshopProduct[]>>,
        workshopProductsInterfaces: WorkshopProductInterface[],
        setWorkshopProductInterfaces: React.Dispatch<React.SetStateAction<WorkshopProductInterface[]>>,
        openState: boolean,
        setOpenState: React.Dispatch<React.SetStateAction<boolean>>
    }) => {
    const { showSnackbar } = useSnackbar();

    const [product, setProduct] = React.useState<Product>();
    const [testCombinations, setTestCombinations] = React.useState<TestCombination[]>([]);
    const [wSProdTestCombinations, setWSProdTestCombinations] = React.useState<WorkshopProductCombination[]>([]);
    const [company, setCompany] = React.useState<Company>();
    const [workshop, setWorkshop] = React.useState<Workshop>();
    const [wsProduct, setwsProduct] = React.useState<WorkshopProduct>();
    const [availableInterfaces, setAvailableInterfaces] = React.useState<Interface[]>([]);
    const [workshopInterfaces, setWorkshopInterfaces] = React.useState<WorkshopInterface[]>([]);
    const [curSelectedWorkshopInterfaces, setCurSelectedWorkshopInterfaces] = React.useState<WorkshopInterface[]>([]);
    const [oldSelectedWorkshopInterfaces, setOldSelectedWorkshopInterfaces] = React.useState<WorkshopInterface[]>([]);

    const showUserFeedback = React.useCallback((msg: string, severity: AlertProps['severity'] = 'error') => {
        showSnackbar(msg, severity);
    }, [showSnackbar]);

    console.log('UpdateWsProductForm2 WorkshopInterfaces: ', workshopProductsInterfaces);

    React.useEffect(() => {
        async function fetch() {
            try {
                if (!openState) {
                    setWorkshopInterfaces([]);

                    setWSProdTestCombinations([]);
                    setTestCombinations([]);

                    setWorkshop(undefined);
                    setProduct(undefined);
                    setAvailableInterfaces([]);
                    setCompany(undefined);
                    setOldSelectedWorkshopInterfaces([]);
                    setCurSelectedWorkshopInterfaces([]);
                    return;
                }
                if (wpId === undefined || wpId === -1) return;
                const workshopProduct = workshopProducts.find(wp => wp.id === wpId)!!;
                if (!workshopProduct) return;
                setwsProduct(structuredClone(workshopProduct));

                const workshopRequest = await getWorkshop(workshopProduct?.workshopId, new AbortController());
                const productRequest = await getProduct(workshopProduct?.productId, new AbortController());
                const wSProdTestCombinationsRequest = await getProductCombinations(workshopProduct?.id, new AbortController());
                const allInterfaces = await enumerateInterfaces(new AbortController());
                const workshopInterfaces = await getWorkshopInterfaces(workshopProduct?.workshopId, new AbortController());
                const testCombinationResponse = await getTestCombinationList(new AbortController());
                const workshopInterfacesResponse = await getWorkshopInterfaces(workshopProduct?.workshopId, new AbortController());

                const companiesRequest = await getCompanies(new AbortController());

                if (workshopRequest.failed) {
                    if (workshopProduct?.workshopId) {
                        workshopRequest.errorText && showUserFeedback(workshopRequest.errorText);
                    }
                    return;
                }
                if (productRequest.failed) {
                    if (workshopProduct?.productId) {
                        productRequest.errorText && showUserFeedback(productRequest.errorText);
                    }
                    return;
                }
                if (workshopInterfaces.failed) {
                    if (workshopProduct?.workshopId) {
                        workshopInterfaces.errorText && showUserFeedback(workshopInterfaces.errorText);
                    }
                    return;
                }
                if (companiesRequest.failed) {
                    companiesRequest.errorText && showUserFeedback(companiesRequest.errorText);
                    return;
                }

                if (wSProdTestCombinationsRequest.failed) {
                    wSProdTestCombinationsRequest.errorText && showUserFeedback(wSProdTestCombinationsRequest.errorText);
                    return;
                }

                if (testCombinationResponse.failed) {
                    testCombinationResponse.errorText && showUserFeedback(testCombinationResponse.errorText);
                    return;
                }

                if (workshopInterfacesResponse.failed) {
                    workshopInterfacesResponse.errorText && showUserFeedback(workshopInterfacesResponse.errorText);
                    return;
                }

                setWorkshopInterfaces(workshopInterfacesResponse.result);

                setWSProdTestCombinations(wSProdTestCombinationsRequest.result);
                setTestCombinations(testCombinationResponse.result);

                const workshopLoc = workshopRequest.result;
                setWorkshop(workshopLoc);
                setProduct(productRequest.result);
                const availableInterfacesLoc = allInterfaces.result.filter(iface => workshopInterfaces.result.find(wi => wi.interfaceId === iface.id));
                setAvailableInterfaces(availableInterfacesLoc);
                setCompany(companiesRequest.result.find(company => company.id === productRequest.result.companyId));

                let newSelectedWorkshopInterfaces = workshopProductsInterfaces.filter(wpi => wpi.workshopProductId === wpId)
                    .map(wpi => { return { workshopId: workshopLoc?.id!!, interfaceId: wpi.interfaceId } });

                setOldSelectedWorkshopInterfaces(newSelectedWorkshopInterfaces);
                setCurSelectedWorkshopInterfaces(newSelectedWorkshopInterfaces);

                console.log('Old selected WorkshopInterfaces: ', newSelectedWorkshopInterfaces);
                console.log('Interfaces: ', availableInterfacesLoc);
            } catch (error) {
                showUserFeedback(String(error));
            }
        };

        fetch();
    }, [wpId, workshopProducts, workshopProductsInterfaces, showUserFeedback, openState]);

    const saveHandler = () => {
        let index = workshopProducts.findIndex(wp => wp.id === wpId);
        let copyWorkshopProductsInterfaces = workshopProductsInterfaces;
        if (wsProduct !== workshopProducts[index]) {
            const newArray = [...workshopProducts];
            newArray[index] = wsProduct as WorkshopProduct;
            updateWorkshopProduct(newArray[index], new AbortController()).then(
                readback => {
                    console.log('Update handler: ', readback);
                    if (!readback.failed) {
                        setWorkshopProducts(newArray);
                        showUserFeedback('Product updated', 'success');
                    }
                    else {
                        showUserFeedback('Update failed: ' + readback.errorText);
                    }
                }
            );
        }

        wSProdTestCombinations.forEach(wspc => {
            if (wspc.workshopProductId === 0) wspc.workshopProductId = wpId;    // newly added combinations will be 0
            if (wspc.workshopProductId === wpId) {                              // safety to ensure nothing slipped thru
                updateProdTestCombination(wspc, new AbortController()).then(
                    readback => {
                        console.log('Update product combination: ', readback);
                        if (!readback.failed) {
                            setWSProdTestCombinations(wSProdTestCombinations.map(wspc2 => wspc2.id === readback.result.id ? readback.result : wspc2));
                            showUserFeedback('Product combination updated', 'success');
                        }
                        else {
                            showUserFeedback('Update product combination failed: ' + readback.errorText);
                        }
                    }
                );
            }
        })

        const newWorkshopInterfaces = curSelectedWorkshopInterfaces.filter(wi => !oldSelectedWorkshopInterfaces.find(owi => owi.interfaceId === wi.interfaceId));
        const removedWorkshopInterfaces = oldSelectedWorkshopInterfaces.filter(owi => !curSelectedWorkshopInterfaces.find(wi => wi.interfaceId === owi.interfaceId));

        // Add new interfaces
        newWorkshopInterfaces.forEach(wi => {
            const newWpi = { workshopProductId: wpId, interfaceId: wi.interfaceId, serverEndpoint: "", supportedByProduct: true };
            updateWorkshopProductInterface(newWpi, new AbortController()).then(
                readback => {
                    console.log('New interface added: ', readback);
                    if (!readback.failed) {
                        copyWorkshopProductsInterfaces = [...copyWorkshopProductsInterfaces, readback.result];
                        setWorkshopProductInterfaces(copyWorkshopProductsInterfaces);
                        showUserFeedback('Product updated', 'success');
                    }
                    else {
                        showUserFeedback('Add interface failed: ' + readback.errorText);
                    }
                }
            );
        });

        // Remove interfaces
        removedWorkshopInterfaces.forEach(wi => {
            const toDelete = workshopProductsInterfaces.filter(wpi => wpi.interfaceId === wi.interfaceId && wpi.workshopProductId === wpId);
            deleteWorkshopProductInterface(toDelete[0], new AbortController()).then(
                readback => {
                    console.log('Delete interface: ', readback);
                    if (!readback.failed) {
                        copyWorkshopProductsInterfaces = copyWorkshopProductsInterfaces.filter(wpi => wpi.id !== toDelete[0].id)
                        setWorkshopProductInterfaces(copyWorkshopProductsInterfaces);
                        showUserFeedback('Product updated', 'success');
                    }
                    else {
                        showUserFeedback('Delete interface failed: ' + readback.errorText);
                    }
                }
            );
        });
        setOpenState(false);
    };

    return <NonRouterModal title='Edit Workshop Product' openState={openState} setOpenState={setOpenState}
        alertComponent={undefined} buttons={[<Button onClick={saveHandler} startIcon={<CheckIcon />}>{'Confirm'}</Button>]}>
        <form id='addWsProductForm'>
            <Stack direction={'column'} spacing={20}>
                <Grid container columnSpacing={30}>
                    <Grid size={6}>
                        <FormControl disabled fullWidth>
                            <InputLabel shrink htmlFor='Workshop name'>{"Workshop name"}</InputLabel>
                            <Input id='Workshop name' value={workshop?.name}></Input>
                        </FormControl>
                    </Grid>
                    <Grid size={6}>
                        <FormControl required fullWidth>
                            <InputLabel shrink htmlFor='Company'>{'Company'}</InputLabel>
                            <Input id='Company' value={company?.name} disabled={true} />
                        </FormControl>
                    </Grid>
                </Grid>

                <Divider />

                <Grid container rowSpacing={20}>
                    <Grid size={6}>
                        <FormControl required fullWidth>
                            <InputLabel shrink htmlFor='Productname'>{'Product Name'}</InputLabel>
                            <Input id='ProductName' value={product?.name} disabled={true} />
                        </FormControl>
                    </Grid>
                </Grid>

                <Divider />

                <Grid container rowSpacing={5} >
                    <Grid size={12}>
                        {testCombinations.map(combo =>
                            (combo.status === TestCombinationStatus.Released) &&
                            < NewCombinationSelection workshopID={workshop?.id ?? 0} testCombination={combo} selectedTestCombinations={wSProdTestCombinations}
                                workshopProductsInterfaces={workshopProductsInterfaces} setSelectedInterfacesForProduct={setCurSelectedWorkshopInterfaces}
                                workshopInterfaces={workshopInterfaces} selectedInterfacesForProduct={curSelectedWorkshopInterfaces}
                                interfaces={availableInterfaces} setSelectedTestCombinations={setWSProdTestCombinations} />
                        )}
                    </Grid>
                </Grid>

                <Divider />

            </Stack>
        </form></NonRouterModal>
}

export const AddProductToWorkshopLocalModal = ({ workshopID, workshopProducts, setWorkshopProducts, workshopProductsInterfaces,
    setWorkshopProductInterfaces, openState, setOpenState }: {
        workshopID: number
        workshopProducts: WorkshopProduct[] | null,
        setWorkshopProducts: React.Dispatch<React.SetStateAction<WorkshopProduct[]>> | null,
        workshopProductsInterfaces: WorkshopProductInterface[] | null,
        setWorkshopProductInterfaces: React.Dispatch<React.SetStateAction<WorkshopProductInterface[]>> | null,
        openState: boolean,
        setOpenState: React.Dispatch<React.SetStateAction<boolean>>
    }) => {
    const userContext = React.useContext(UserContext);
    const { showSnackbar } = useSnackbar();

    const [companiesProducts, setCompaniesProducts] = React.useState<Map<Company, Product[]>>(new Map());
    const [testCombinations, setTestCombinations] = React.useState<TestCombination[]>([]);
    const [selectedCompany, setSelectedCompany] = React.useState<Company | null>(null);
    const [selectedProduct, setSelectedProduct] = React.useState<Product | null>(null);
    const [selectedTestCombinations, setSelectedTestCombinations] = React.useState<WorkshopProductCombination[]>([]);

    const [workshop, setWorkshop] = React.useState<Workshop>();
    const [workshopInterfaces, setWorkshopInterfaces] = React.useState<WorkshopInterface[]>([]);

    const [interfaces, setInterfaces] = React.useState<Interface[]>([]);

    const [newProductName, setNewProductName] = React.useState<string>("");
    const [isServerProduct, setIsServerProduct] = React.useState<boolean>(false);
    const [isClientProduct, setIsClientProduct] = React.useState<boolean>(false);
    const [selectedInterfacesForProduct, setSelectedInterfacesForProduct] = React.useState<WorkshopInterface[]>([]);

    const showUserFeedback = React.useCallback((msg: string, severity: AlertProps['severity'] = 'error') => {
        showSnackbar(msg, severity);
    }, [showSnackbar]);

    const addWorkshopProductHandler = async () => {
        let selectedProductLoc = selectedProduct;
        if (selectedProductLoc === null && newProductName !== "") {
            let selectedProduct2 = await addNewProductHandler();
            if (selectedProduct2 !== undefined) selectedProductLoc = selectedProduct2;
        }
        const newRecord = {
            productId: selectedProductLoc?.id!!, workshopId: workshop?.id!!,
            isClient: isClientProduct, isServer: isServerProduct,
            clientReadyToTest: false, serverReadyToTest: false,
            status: WorkshopProductStatus.Available, releaseStatus: ProductReleaseStatus.Unknown
        };

        try {
            const readback: ApiResponse<WorkshopProduct> = await httpPost('/api/workshopproduct/update', newRecord, new AbortController());
            if (readback.failed) {
                readback.errorText && showUserFeedback(readback.errorText);
                return;
            }
            const newWorkshopProduct = readback.result;
            setWorkshopProducts && setWorkshopProducts([...workshopProducts ?? [], newWorkshopProduct]);

            // insert the selected test combinations
            const wpComboPromises = selectedTestCombinations.map(wtc => updateProdTestCombination(
                {
                    isConsumer: wtc.isConsumer, isProvider: wtc.isProvider, providerReadyToTest: false, consumerReadyToTest: false,
                    testCombinationId: wtc.testCombinationId, workshopProductId: newWorkshopProduct.id
                }, new AbortController()));
            const wpComboresponses = await Promise.all(wpComboPromises);

            let someFailed = wpComboresponses.find(response => response.failed);
            if ((someFailed)) {
                someFailed.errorText && showUserFeedback(someFailed.errorText);
                return;
            }

            // insert the selected interfaces
            const wpPromises = selectedInterfacesForProduct.map(wi => updateWorkshopProductInterface(
                { interfaceId: wi.interfaceId, workshopProductId: newWorkshopProduct.id, supportedByProduct: true, serverEndpoint: '' }, new AbortController()));
            const responses = await Promise.all(wpPromises);

            someFailed = responses.find(response => response.failed);
            if ((someFailed)) {
                someFailed.errorText && showUserFeedback(someFailed.errorText);
                return;
            }
            showUserFeedback('Product added', "success");

            let newworkshopProductsInterfaces = workshopProductsInterfaces ?? [];
            responses.forEach(res => newworkshopProductsInterfaces.push(res.result));
            setWorkshopProductInterfaces && setWorkshopProductInterfaces(newworkshopProductsInterfaces);

            setSelectedProduct(null);
            setSelectedTestCombinations([]);
            setSelectedInterfacesForProduct([]);
            setIsServerProduct(false);
            setIsClientProduct(false);
            setOpenState(false);
        } catch (error) {
            showUserFeedback(String(error));
        }
    }

    React.useEffect(() => {
    }, [workshopID, userContext, showUserFeedback]);

    const addNewProductHandler = async () => {
        const newRecord = {
            name: newProductName, companyId: selectedCompany?.id!!, status: 1
        };
        let newProduct: Product = { id: 0, guid: "", name: "", companyId: 0, status: 0 };

        try {
            const readback: ApiResponse<Product> = await httpPost('/api/product/update', newRecord, new AbortController());
            if (readback.failed) {
                setNewProductName("");
                readback.errorText && showUserFeedback(readback.errorText);
                return;
            }

            let copycompaniesProducts = companiesProducts;
            copycompaniesProducts.get(selectedCompany!!)?.push(readback.result);
            setCompaniesProducts(copycompaniesProducts);
            newProduct = readback.result;
            setSelectedProduct(newProduct);
            setNewProductName("");
            showUserFeedback('new Product Created', "success");
        } catch (error) {
            showUserFeedback(String(error));
        }
        return newProduct as Product;
    }

    React.useEffect(() => {
        const fetch = async () => {
            try {
                const workshopResponse = await getWorkshop(Number(workshopID), new AbortController());
                const companiesResponse = await getCompanies(new AbortController());
                const workshopInterfacesResponse = await getWorkshopInterfaces(Number(workshopID), new AbortController());
                const interfacesResponse = await enumerateInterfaces(new AbortController());
                const productsResponse = await getProducts(new AbortController());
                const testCombinationResponse = await getTestCombinationList(new AbortController());
                //export const getTestComboSupport = (data: TestComboSupportVal

                if (workshopResponse.failed) {
                    workshopResponse.errorText && showUserFeedback(workshopResponse.errorText);
                    return;
                }
                setWorkshop(workshopResponse.result);

                if (testCombinationResponse.failed) {
                    testCombinationResponse.errorText && showUserFeedback(testCombinationResponse.errorText);
                    return;
                }
                setTestCombinations(testCombinationResponse.result);

                if (companiesResponse.failed) {
                    companiesResponse.errorText && showUserFeedback(companiesResponse.errorText);
                    return;
                }
                if (productsResponse.failed) {
                    productsResponse.errorText && showUserFeedback(productsResponse.errorText);
                }
                const cmpnyProds = new Map<Company, Product[]>();
                companiesResponse.result.forEach(cmpny => cmpnyProds.set(cmpny, productsResponse.result.filter(product => product.companyId === cmpny.id)));
                setCompaniesProducts(cmpnyProds);
                setSelectedCompany(companiesResponse.result.find(cmpny => cmpny.name === userContext.user.companyName) ?? null);

                if (workshopInterfacesResponse.failed) {
                    workshopInterfacesResponse.errorText && showUserFeedback(workshopInterfacesResponse.errorText);
                    return;
                }
                setWorkshopInterfaces(workshopInterfacesResponse.result);

                if (interfacesResponse.failed) {
                    interfacesResponse.errorText && showUserFeedback(interfacesResponse.errorText);
                    return;
                }
                setInterfaces(interfacesResponse.result);
            } catch (error) {
                showUserFeedback(String(error));
            }
        }

        fetch();
    }, [workshopID, userContext, showUserFeedback]);

    const filter = createFilterOptions<Product>();

    return <NonRouterModal title='Add Workshop Product' openState={openState} setOpenState={setOpenState}
        alertComponent={undefined} buttons={[<Button onClick={addWorkshopProductHandler} startIcon={<CheckIcon />}>{'Confirm'}</Button>]}>
        <form id='addWsProductForm'>
            <Stack direction={'column'} spacing={20}>
                <Grid container columnSpacing={10}>
                    <Grid size={6}>
                        <FormControl disabled fullWidth>
                            <InputLabel shrink htmlFor='Workshop name'>{"Workshop name"}</InputLabel>
                            <Input id='Workshop name' value={workshop?.name}></Input>
                        </FormControl>
                    </Grid>
                    <Grid size={6}>
                        <FormControl required fullWidth>
                            <Autocomplete id='Company' options={[...companiesProducts.keys()]} value={selectedCompany} disabled={true}
                                renderInput={(params) => <TextField multiline {...params} label={"Company"} />}
                                getOptionLabel={company => company.name}
                                onChange={(event, value) => setSelectedCompany(value)} />
                        </FormControl>
                    </Grid>
                </Grid>

                <Divider />
                <Grid container rowSpacing={20}>
                    <Grid size={5}>
                        <FormControl required fullWidth>
                            <Autocomplete
                                id='ProductName'
                                options={companiesProducts.get(selectedCompany!!) ?? []}
                                value={selectedProduct} disabled={false}
                                renderInput={(params) => <TextField multiline {...params} label={"Product name"} />}
                                renderOption={(props, product) => {
                                    return (
                                        <li {...props} key={product.id}>
                                            {product.name}
                                        </li>
                                    );
                                }}
                                getOptionLabel={(product) => {
                                    // Value selected with enter, right from the input
                                    if (typeof product === 'string') {
                                        return product;
                                    }
                                    // Regular option
                                    return product.name;
                                }}
                                onChange={(event, newValue) => {
                                    if (typeof newValue === 'string') {
                                        addNewProductHandler();
                                    }
                                    else if (newValue && newValue.id === 0) {
                                        // Create a new value from the user input
                                        addNewProductHandler();
                                    }
                                    else {
                                        setSelectedProduct(newValue);
                                    }
                                }}
                                filterOptions={(options, params) => {
                                    const filtered = filter(options, params);

                                    const { inputValue } = params;
                                    // Suggest the creation of a new value
                                    const isExisting = options.some((option) => inputValue === option.name);
                                    if (inputValue !== '' && !isExisting) {
                                        setNewProductName(inputValue);
                                        filtered.push({
                                            //name: inputValue,
                                            name: `Add "${inputValue}" as a new Product`, id: 0, guid: "", companyId: 0, status: 0,
                                        });
                                    }

                                    return filtered;
                                }}
                                freeSolo

                            />
                        </FormControl>
                    </Grid>

                    <Grid size={4} padding={10} sx={{ visibility: "hidden" }}>
                        <Typography variant='body2'>{"If you are missing a product name, add new product names to the database!"}</Typography>
                    </Grid>

                    <Grid size={3} sx={{ visibility: "hidden" }}>
                        <Stack direction='row' paddingBottom={0}>
                            <FormControl>
                                <Input id='New product name' value={newProductName} disabled={false}
                                    onChange={(e) => setNewProductName(e.target.value)} />
                            </FormControl>
                            <IconButton disabled={false} onClick={() => {
                                addNewProductHandler();
                            }}><AddIcon /></IconButton>
                        </Stack>
                    </Grid>
                </Grid>

                <Divider />

                <Grid size={12} container rowSpacing={5} >
                    {testCombinations.map(combo =>
                        (combo.status === TestCombinationStatus.Released) &&
                        < NewCombinationSelection workshopID={workshopID} testCombination={combo} selectedTestCombinations={selectedTestCombinations}
                            workshopProductsInterfaces={workshopProductsInterfaces} setSelectedInterfacesForProduct={setSelectedInterfacesForProduct}
                            workshopInterfaces={workshopInterfaces} selectedInterfacesForProduct={selectedInterfacesForProduct} 
                            interfaces={interfaces} setSelectedTestCombinations={setSelectedTestCombinations} />
                    )}
                </Grid>
                
                <Divider />
            </Stack>
        </form></NonRouterModal>
}

const NewCombinationSelection = ({ workshopID, testCombination, selectedTestCombinations, workshopInterfaces,
    selectedInterfacesForProduct, setSelectedInterfacesForProduct,
    interfaces, workshopProductsInterfaces, setSelectedTestCombinations }:
    {
        workshopID: number,
        testCombination: TestCombination,
        interfaces: Interface[],
        workshopInterfaces: WorkshopInterface[],
        selectedInterfacesForProduct: WorkshopInterface[],
        selectedTestCombinations: WorkshopProductCombination[],
        workshopProductsInterfaces: WorkshopProductInterface[] | null,
        setSelectedInterfacesForProduct: React.Dispatch<React.SetStateAction<WorkshopInterface[]>>,
        setSelectedTestCombinations: (value: React.SetStateAction<WorkshopProductCombination[]>) => void
    }) => {
    const { showSnackbar } = useSnackbar();
    const [wSProdTestCombination, setWSProdTestCombination] = React.useState<WorkshopProductCombination>(GetEmptyWSPTC());
    const [testComboSupportVals, setTestComboSupportVals] = React.useState<TestComboSupportVal[]>([]);
    const [supportedInterfaces, setSupportedInterfaces] = React.useState<WorkshopInterface[]>([]);

    const showUserFeedback = React.useCallback((msg: string, severity: AlertProps['severity'] = 'error') => {
        showSnackbar(msg, severity);
    }, [showSnackbar]);

    React.useEffect(() => {
        const fetch = async () => {
            let wsProdCombo = selectedTestCombinations.find(tc => testCombination.id === tc.testCombinationId) ?? GetEmptyWSPTC();
            const ifaceSupport: TestComboSupportVal = { testCombinationId: testCombination.id, interfacePropertyId: 0, testCaseId: 0 };

            setWSProdTestCombination(wsProdCombo);

            const ifaceSupportResponse = await getTestComboSupport(ifaceSupport, new AbortController());

            if (ifaceSupportResponse.failed) {
                ifaceSupportResponse.errorText && showUserFeedback(ifaceSupportResponse.errorText);
                return;
            }
            setTestComboSupportVals(ifaceSupportResponse.result);
            setSupportedInterfaces(workshopInterfaces.filter(wsiface => ifaceSupportResponse.result.find(supportVal => supportVal.interfaceId === wsiface.interfaceId)));
        }
        fetch();
    }, [testCombination, workshopInterfaces, selectedTestCombinations, showUserFeedback]);

    const serverOnChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
        let selectedTestCombinationsLoc = selectedTestCombinations;
        const index = selectedTestCombinationsLoc.findIndex(tc => tc.testCombinationId === testCombination.id);
        if (index !== -1) {
            selectedTestCombinationsLoc[index] = { ...selectedTestCombinationsLoc[index], isProvider: e.target.checked };
        } else {
            selectedTestCombinationsLoc.push({
                workshopProductId: 0, testCombinationId: testCombination.id ?? 0, isProvider: e.target.checked,
                isConsumer: false, providerReadyToTest: false, consumerReadyToTest: false
            });
        }
        let wsProdCombo = selectedTestCombinationsLoc.find(tc => testCombination.id === tc.testCombinationId) ?? GetEmptyWSPTC();

        setWSProdTestCombination(wsProdCombo);
        setSelectedTestCombinations(selectedTestCombinationsLoc);
    }

    const clientOnChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
        let selectedTestCombinationsLoc = selectedTestCombinations;
        const index = selectedTestCombinationsLoc.findIndex(tc => tc.testCombinationId === testCombination.id);
        if (index !== -1) {
            selectedTestCombinationsLoc[index] = { ...selectedTestCombinationsLoc[index], isConsumer: e.target.checked };
        } else {
            selectedTestCombinationsLoc.push({
                workshopProductId: 0, testCombinationId: testCombination.id ?? 0, isConsumer: e.target.checked,
                isProvider: false, providerReadyToTest: false, consumerReadyToTest: false
            });
        }
        let wsProdCombo = selectedTestCombinationsLoc.find(tc => testCombination.id === tc.testCombinationId) ?? GetEmptyWSPTC();

        setWSProdTestCombination(wsProdCombo);
        setSelectedTestCombinations(selectedTestCombinationsLoc);
    }

    return <React.Fragment>
        { (supportedInterfaces && supportedInterfaces.length > 0) ?
            <Paper sx={{ 'width': "100%" }} elevation={6} >
                <Grid container size={12}>
                    <Grid size={4} mt={7} >
                        <Typography variant='subtitle1' key={testCombination.id + "n"}>{testCombination.combinationName}</Typography>
                    </Grid>
                    <Grid size={2} >
                        <FormControlLabel key={testCombination.id + "p"}
                            control={<Checkbox checked={wSProdTestCombination?.isProvider}
                                onChange={(e) => { serverOnChangeHandler(e); }}
                                inputProps={{ 'aria-label': 'controlled' }} />} label={testCombination.combinationProvider} />
                    </Grid>
                    <Grid size={2} >
                        <FormControlLabel key={testCombination.id + "c"}
                            control={<Checkbox checked={wSProdTestCombination?.isConsumer}
                                onChange={(e) => { clientOnChangeHandler(e); }}
                                inputProps={{ 'aria-label': 'controlled' }} />} label={testCombination.combinationConsumer} />
                    </Grid>
                    <Grid size={4}>
                        {supportedInterfaces && supportedInterfaces.map(wsiface =>
                            <CombinationInterfaces workshopInterface={wsiface} iface={interfaces.find(i => i.id === wsiface.interfaceId)}
                                selectedInterfacesForProduct={selectedInterfacesForProduct}
                                disabled={!wSProdTestCombination?.isProvider && !wSProdTestCombination?.isConsumer}
                                setSelectedInterfacesForProduct={setSelectedInterfacesForProduct} />
                        )}
                    </Grid>
                </Grid>
            </Paper> : <></>
        }
    </React.Fragment>
}

const CombinationInterfaces = ({ workshopInterface, iface, disabled, selectedInterfacesForProduct, setSelectedInterfacesForProduct }:
    {
        workshopInterface: WorkshopInterface,
        disabled: boolean,
        iface: Interface | undefined,
        selectedInterfacesForProduct: WorkshopInterface[],
        setSelectedInterfacesForProduct: React.Dispatch<React.SetStateAction<WorkshopInterface[]>>,
    }) => {

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        let newSelectedInterfaces: WorkshopInterface[] = [...selectedInterfacesForProduct];
        if (event.target.checked) {
            newSelectedInterfaces.push(workshopInterface);
        }
        else {
            newSelectedInterfaces = newSelectedInterfaces.filter(si => si.interfaceId !== workshopInterface.interfaceId);
        }
        setSelectedInterfacesForProduct(newSelectedInterfaces);
    };

    return <>
        <FormControlLabel label={iface?.name ?? 'Error'}
            control={<BlueSwitch onChange={handleChange} disabled={disabled}
                checked={selectedInterfacesForProduct.find(si => si.interfaceId === workshopInterface.interfaceId) ? true : false} />} />
    </>
}
